import { Activity, Check, CircleCheck, Users, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Input,
  LoadingState,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { ASSISTANCE_LABELS } from '@/features/patients/types';
import { useQueue, useQueueActions } from '@/features/queue/api';
import type { QueueTicket } from '@/features/queue/types';
import { todayKl } from '@/pages/calendar/time';
import { PatientDetailsDrawer } from '@/pages/patients/PatientDetailsDrawer';
import { AppArrivalBadges } from './AppCheckInSummary';
import { CancelTicketDialog } from './CancelTicketDialog';
import { UNDO_MS, useDeferredActions } from './use-deferred-actions';
import { CoverCheckDrawer } from './CoverCheckDrawer';
import { callNextFailure } from './call-next-error';
import { RoomDialog } from './RoomDialog';
import { SeeFirstDrawer } from './SeeFirstDrawer';
import { VitalsDrawer } from './VitalsDrawer';
import { vitalsTargetOf } from './queue-format';
import { clock, ticketLine } from './queue-format';
import styles from './queue.module.css';

/** One status vocabulary for every queue view: Waiting → In room N → Done · time. */
export function TicketStatusBadge({ t }: { t: QueueTicket }) {
  if (t.status === 'in_room')
    return <Badge tone="success" dot>{`In ${t.room ?? 'room'}`}</Badge>;
  if (t.status === 'done')
    return (
      <Badge
        icon={<Check size={12} />}
      >{`Done · ${t.doneAt ? clock(t.doneAt) : ''}`}</Badge>
    );
  if (t.status === 'cancelled') return <Badge>Cancelled</Badge>;
  if (t.seeFirst) return <Badge tone="danger">See first</Badge>;
  if (t.source === 'appointment') {
    // RL-1668 (gap #134): App only for bookings made in the app.
    const where =
      t.bookedIn === 'app'
        ? 'App'
        : t.bookedIn === 'clinic'
          ? 'Counter'
          : 'Booked';
    return (
      <Badge tone="info">{t.arrivedLate ? `${where} · late` : where}</Badge>
    );
  }
  return <Badge>Walk-in</Badge>;
}

/**
 * The live queue cards (Admin v2 #7–#9). Each card shows the ticket, payer,
 * assistance needs and whether vitals are done; done cards stay in the grid,
 * greyed. Actions: Vitals, Check cover, See first, Call, Done, Cancel
 * ticket (RL-1658, both with a 10 s Undo), Details.
 */
export function QueueBoard({
  title,
  dayPicker = false,
}: {
  title?: string;
  /** RL-2183: let staff open a past day (read-only). Off on the dashboard. */
  dayPicker?: boolean;
}) {
  const { t } = useTranslation();
  /** The translator inside the card loop, where `t` is the ticket. */
  const tr = t;
  const session = useSession();
  // RL-2183: only 'today' is operable; a past day is history, no actions.
  const [day, setDay] = useState<string | null>(null);
  const past = day !== null;
  const hasPermission = (p: Parameters<typeof session.hasPermission>[0]) =>
    session.hasPermission(p) && (!past || p === 'patients:read');
  const { data, isLoading, error, refetch } = useQueue(true, day ?? undefined);
  const { selected: branch } = useBranch();
  const a = useQueueActions();
  const toast = useToast();
  const [vitalsFor, setVitalsFor] = useState<QueueTicket | null>(null);
  const [seeFirstFor, setSeeFirstFor] = useState<QueueTicket | null>(null);
  const [coverFor, setCoverFor] = useState<QueueTicket | null>(null);
  const [callFor, setCallFor] = useState<QueueTicket | 'next' | null>(null);
  const [details, setDetails] = useState<string | null>(null);
  const [cancelFor, setCancelFor] = useState<QueueTicket | null>(null);
  // RL-1658: Cancel ticket and Done wait 10 s for Undo before they are sent.
  const deferred = useDeferredActions();

  const fail = (err: unknown) =>
    toast.show({ tone: 'error', ...callNextFailure(err, t), error: err });

  // Active first (in room, then waiting in queue order), then the done cards.
  const tickets = (data?.tickets ?? []).filter(
    (t) => t.status !== 'cancelled' && !deferred.hidden.has(t.id),
  );

  /** RL-1658: take the card away now; send after 10 s unless undone. */
  function later(
    ticket: QueueTicket,
    kind: 'cancel' | 'done',
    send: (o: { keepalive: boolean }) => Promise<unknown>,
  ) {
    deferred.defer(ticket.id, (o) => send(o).catch(fail));
    toast.show(
      {
        tone: 'success',
        title: t(`queue.${kind}.toast`, { number: ticket.number }),
        body: t(`queue.${kind}.toastBody`),
        action: {
          label: t('queue.undo'),
          onClick: () => deferred.undo(ticket.id),
        },
        // Review: the Undo window waits while the toast is hovered or focused.
        onPause: () => deferred.pause(ticket.id),
        onResume: () => deferred.resume(ticket.id),
      },
      UNDO_MS,
    );
  }

  async function confirmCall(room: string) {
    try {
      const t =
        callFor === 'next'
          ? await a.callNext.mutateAsync(room)
          : await a.call.mutateAsync({ id: (callFor as QueueTicket).id, room });
      toast.show({
        tone: 'success',
        title: `Number ${t.number} called to ${t.room}`,
        body: 'The waiting-room screen shows the number with a chime.',
      });
      setCallFor(null);
    } catch (err) {
      fail(err);
    }
  }

  return (
    <Card
      title={title}
      actions={
        <>
          {dayPicker && (
            <Input
              type="date"
              aria-label="Queue day"
              max={todayKl()}
              value={day ?? todayKl()}
              onChange={(e) => {
                const v = e.target.value;
                setDay(!v || v >= todayKl() ? null : v);
              }}
            />
          )}
          <span className={styles.sub}>
            {past
              ? `History for ${day}: read-only`
              : 'One queue for app check-ins and walk-ins'}
          </span>
          {hasPermission('queue:call') && (
            <Button
              size="sm"
              variant="primary"
              icon={<Check size={14} />}
              onClick={() => setCallFor('next')}
            >
              Call next
            </Button>
          )}
        </>
      }
    >
      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {data && tickets.length === 0 && (
        <p className={styles.sub}>
          {past
            ? `Nobody was queued on ${day}.`
            : 'Nobody in the queue yet today.'}
        </p>
      )}
      <div className={styles.cards}>
        {tickets.map((t) => {
          const done = t.status === 'done';
          return (
            <article
              key={t.id}
              aria-label={`Ticket ${t.number}`}
              className={[
                styles.ticket,
                t.status === 'in_room' && styles.inRoom,
                t.seeFirst && t.status === 'waiting' && styles.urgent,
                done && styles.doneCard,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <header className={styles.ticketHead}>
                <span className={styles.number}>{t.number}</span>
                <TicketStatusBadge t={t} />
              </header>
              <p className={styles.name}>{t.patient.fullName}</p>
              <p className={styles.sub}>{ticketLine(t)}</p>
              {!done && (
                <div className={styles.badges}>
                  {t.patient.assistanceNeeds.map((n) => (
                    <Badge key={n} tone="info" icon={<Users size={12} />}>
                      {ASSISTANCE_LABELS[n].split(':')[0]}
                    </Badge>
                  ))}
                  {t.cover && (
                    <Badge
                      tone={t.cover.status === 'covered' ? 'success' : 'danger'}
                    >
                      {t.cover.status === 'covered'
                        ? 'Cover checked'
                        : 'Not covered'}
                    </Badge>
                  )}
                </div>
              )}
              {!done && (
                <AppArrivalBadges
                  lateNotice={t.lateNotice}
                  appCheckIn={t.appCheckIn}
                  withAnswers
                />
              )}
              <div className={styles.ticketActions}>
                {!done && hasPermission('vitals:record') && (
                  <Button
                    size="sm"
                    variant={t.vitals ? 'ghost' : 'secondary'}
                    icon={<Activity size={14} />}
                    onClick={() => setVitalsFor(t)}
                  >
                    {t.vitals ? 'Vitals' : 'Take vitals'}
                  </Button>
                )}
                {/* RL-1664: also for a self-pay visit with a membership typed in the app. */}
                {!done &&
                  (t.payer || t.appCover) &&
                  hasPermission('queue:manage') && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setCoverFor(t)}
                    >
                      Check cover
                    </Button>
                  )}
                {t.status === 'waiting' && hasPermission('queue:manage') && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSeeFirstFor(t)}
                  >
                    See first
                  </Button>
                )}
                {t.status === 'waiting' && hasPermission('queue:call') && (
                  <Button size="sm" onClick={() => setCallFor(t)}>
                    Call
                  </Button>
                )}
                {!done &&
                  t.encounter?.status !== 'open' &&
                  hasPermission('queue:manage') && (
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<CircleCheck size={14} />}
                      title={tr('queue.done.hint')}
                      onClick={() =>
                        later(t, 'done', ({ keepalive }) =>
                          a.done.mutateAsync({ id: t.id, keepalive }),
                        )
                      }
                    >
                      {tr('queue.done.action')}
                    </Button>
                  )}
                {t.status === 'waiting' && hasPermission('queue:manage') && (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<X size={14} />}
                    onClick={() => setCancelFor(t)}
                  >
                    {tr('queue.cancel.action')}
                  </Button>
                )}
                {hasPermission('patients:read') && (
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => setDetails(t.patient.id)}
                  >
                    Details
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>

      <VitalsDrawer
        key={vitalsFor?.id ?? 'none'}
        ticket={vitalsFor && vitalsTargetOf(vitalsFor)}
        busy={a.recordVitals.isPending}
        onClose={() => setVitalsFor(null)}
        onSave={(vitals) =>
          vitalsFor &&
          a.recordVitals
            .mutateAsync({ id: vitalsFor.id, vitals })
            .then((v) => {
              toast.show({
                tone: v.flags.length ? 'info' : 'success',
                title: 'Vitals saved to today’s visit',
                body:
                  v.flags.map((f) => f.label).join(', ') || 'All within range',
              });
              setVitalsFor(null);
            })
            .catch(fail)
        }
      />
      <SeeFirstDrawer
        key={seeFirstFor?.id ?? 'none-sf'}
        ticket={seeFirstFor}
        busy={a.seeFirst.isPending}
        onClose={() => setSeeFirstFor(null)}
        onConfirm={(flag, note) =>
          seeFirstFor &&
          a.seeFirst
            .mutateAsync({ id: seeFirstFor.id, flag, note: note || undefined })
            .then(() => {
              toast.show({
                tone: 'success',
                title: `${seeFirstFor.patient.fullName} moved to the front`,
                body: 'The doctors are alerted (bell).',
              });
              setSeeFirstFor(null);
            })
            .catch(fail)
        }
      />
      <CoverCheckDrawer
        key={coverFor?.id ?? 'none-cover'}
        ticket={coverFor}
        onClose={() => setCoverFor(null)}
      />
      <CancelTicketDialog
        key={cancelFor?.id ?? 'none-cancel'}
        ticket={cancelFor}
        onClose={() => setCancelFor(null)}
        onConfirm={(reason) => {
          const ticket = cancelFor!;
          setCancelFor(null);
          later(ticket, 'cancel', ({ keepalive }) =>
            a.cancel.mutateAsync({ id: ticket.id, reason, keepalive }),
          );
        }}
      />
      <RoomDialog
        open={callFor !== null}
        title={
          callFor === 'next' ? 'Call next' : `Call ${callFor?.number ?? ''}`
        }
        branchName={callFor === 'next' ? branch?.name : undefined}
        busy={a.call.isPending || a.callNext.isPending}
        onClose={() => setCallFor(null)}
        onConfirm={(room) => void confirmCall(room)}
      />
      <PatientDetailsDrawer
        patientId={details}
        onClose={() => setDetails(null)}
      />
    </Card>
  );
}
